﻿import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';

import { CheckmarkIcon, XmarkIcon } from '@dynatrace/strato-icons';

import {
  switchIconCSS,
  switchLabelCSS,
  switchTextCSS,
  switchThumbCSS,
  switchWrapperCSS,
} from './Switch.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useAriaLabelingProps as _useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getI18nValidationState,
  useFormFieldValidation,
} from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';

function getSwitchFieldVariant(isSelected: boolean, showError: boolean) {
  if (isSelected) {
    return 'accent';
  }
  if (showError) {
    return 'emphasized';
  }
  return 'default';
}

const COMPONENT_NAME = 'Switch';

// #region Types
/**
 * @public
 */
export type SwitchProps = FormControlProps<
  boolean,
  (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void | never
> &
  StylingProps &
  DataTestId &
  MaskingProps &
  BehaviorTrackingProps &
  WithChildren & {
    /** The string used as the value of the switch when submitting it in a form, if the switch is toggled on. */
    formValue?: string;
  };
// #endregion

/**
 * The `Switch` component allows users to toggle between two different states. You can
 * set a specific initial value using the `defaultValue` prop for an uncontrolled switch
 * or setting the initial value of the state of a controlled switch accordingly. The `name` prop also
 * needs to be specified for identifying the `Switch` when submitting it in a form.
 * @public
 */
export const Switch = forwardRef<FormControlRef, SwitchProps>(
  (props, forwardedRef) => {
    const {
      value,
      defaultValue,
      children,
      controlState,
      name,
      formValue,
      onValidityChange,
      onChange,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      id: propId,
      required: propRequired,
      disabled: propDisabled,
      'aria-disabled': ariaDisabledProp,
      readOnly: propReadOnly,
      ...remainingProps
    }: SwitchProps = props;

    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    const { state: messagesContextState, dispatch: messagesContextDispatch } =
      useFormFieldMessagesContext();

    const { disabled, required, id, ariaDisabled, readOnly } =
      useSyncedFormControlProps({
        propDisabled,
        propRequired,
        propId,
        propAriaDisabled: ariaDisabledProp,
        propReadOnly,
      });

    const disabledOrAriaDisabled = disabled || ariaDisabled || false;

    const ariaLabelingProps = _useAriaLabelingProps(props);

    const [isSelected, setIsSelected] = useControllableState({
      value,
      defaultValue,
    });

    // Imperative Handle Construction
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const _emitValidationState = useValidationStateEmitter(onValidityChange);
    const emitValidationState = useCustomMessageEmitter(
      _emitValidationState,
      () => getI18nValidationState(intl, inputRef.current, 'manual'),
    );

    useImperativeHandle(forwardedRef, (): FormControlRef<HTMLDivElement> => {
      return {
        element: rootRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          inputRef.current?.checkValidity();
          messagesContextDispatch?.({
            type: FormFieldMessagesAction.SETISTOUCHED,
            payload: { isTouched: true },
          });
          return emitValidationState(
            getI18nValidationState(intl, inputRef.current, 'manual'),
          );
        },
      };
    });

    const {
      onChange: validationOnChange,
      onBlur: validationOnBlur,
      onInvalid: validationOnInvalid,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { required, value: String(value), isSelected },
      undefined,
      undefined,
      _emitValidationState,
    );

    const [controlStateHasError, hint] = useFormControlState(controlState);

    const showError = controlStateHasError || messagesContextState.showError;

    // _useInputHint will be replaced with new validation concept. APPDEV-8298
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { inputProps, hintProps } = useInputHint({
      hint,
      hasError: showError,
    });

    if (value !== undefined && !onChange && isDev()) {
      // eslint-disable-next-line no-console
      console.log(
        'Defining "value" makes the switch controlled. Set the onChange() callback and implement the toggle functionality.',
      );
    }

    const color = isSelected ? 'primary' : 'neutral';
    let focusRingColor: 'critical' | 'primary' | 'neutral' = 'neutral';

    if (showError) {
      focusRingColor = 'critical';
    } else if (!(readOnly || disabledOrAriaDisabled) && isSelected) {
      focusRingColor = 'primary';
    }

    const { focusClassName, focusProps, isFocusVisible } = useFocusRing({
      color: focusRingColor,
      disabled,
    });

    /** The callback function when the state changes */
    function handleChange(e?: ChangeEvent<HTMLInputElement>): void {
      if (!disabled && !readOnly) {
        onChange?.(!isSelected, e);
        setIsSelected(!isSelected);
        !controlState && validationOnChange();
      }
    }

    return (
      <Flex
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        flexDirection="column"
        gap={4}
        className={consumerClassName}
        style={consumerStyle}
        {...behavioralTrackingProps}
        data-dt-component={COMPONENT_NAME}
      >
        <label
          htmlFor={id}
          className={clsx(
            switchLabelCSS({ disabled: disabledOrAriaDisabled, readOnly }),
          )}
        >
          <VisuallyHidden>
            <input
              id={id}
              type="checkbox"
              role="switch"
              checked={isSelected}
              aria-disabled={ariaDisabled}
              disabled={disabled}
              required={required}
              readOnly={readOnly}
              aria-checked={isSelected}
              onChange={handleChange}
              onBlur={(event) => {
                focusProps.onBlur(event);
                !controlState && validationOnBlur(event);
              }}
              onFocus={focusProps.onFocus}
              onInvalid={(event) => {
                !controlState && validationOnInvalid(event);
              }}
              name={name}
              value={formValue}
              ref={inputRef}
              {..._mergeProps(
                controlState ? inputProps : ariaProps,
                ariaLabelingProps,
              )}
            />
          </VisuallyHidden>
          <div
            className={clsx(
              focusClassName,
              switchWrapperCSS({
                disabled: disabledOrAriaDisabled,
                color,
                hasError: showError,
                isFocusVisible,
                readOnly,
              }),
              fieldColorsCSS({
                disabled: disabledOrAriaDisabled,
                color: showError ? 'critical' : color,
                variant: getSwitchFieldVariant(isSelected, showError),
              }),
            )}
          >
            <div
              className={switchIconCSS({
                selected: isSelected,
                readOnly,
                hasError: showError,
              })}
            >
              <CheckmarkIcon size="small" />
              <div className={switchThumbCSS} />
              <XmarkIcon size="small" />
            </div>
          </div>
          <span
            className={switchTextCSS({
              disabled: disabledOrAriaDisabled,
              required: !!children && required,
            })}
          >
            {children}
          </span>
        </label>
        {hint && (
          <BaseItem
            variant={showError ? 'error' : 'hint'}
            disabled={disabledOrAriaDisabled}
            {...hintProps}
          >
            {hint}
          </BaseItem>
        )}
      </Flex>
    );
  },
);
(Switch as typeof Switch & { displayName: string }).displayName =
  COMPONENT_NAME;
